import React, { useState, useEffect } from "react";
import {Button, Dialog, DialogActions, DialogContent, Grid, Typography} from "@mui/material";
import { Grid as DataGrid, GridColumn } from "@progress/kendo-react-grid";
import { orderBy } from "@progress/kendo-data-query";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { CurrencyCell } from "../currency-cell";
import FrequencyDropDown from "../frequency-dropdown";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import {collectErrorMessages, getCurrentYear} from "../../shared/shared";
import YearCell from "../year-cell";
import IconButton from "@mui/material/IconButton";
import ErrorIcon from "@mui/icons-material/Error";
import {StyledTooltip} from "../../pages/plan-inputs";
import ValidationSignCell from "../validation-sign-cell";
import OwnerJointTrustDropDown from "../owner-joint-trust-cell";
import {INCOME_TYPES} from "../../shared/constants";
import { Input } from "@progress/kendo-react-inputs";
import TrustNameDialog from "../trust-name-dialog";

const CommandCell = (deleteFn) => {
  return (props) => {
    const dataItem = props.dataItem;

    return (
      <td>
        <DeleteIcon
          onClick={() => {
            deleteFn(dataItem);
          }}
        />
      </td>
    );
  };
};

const IncomeTypeDropDown = (props) => {
  const handleChange = (e) => {
    if (props.onChange) {
      props.onChange({
        dataIndex: 0,
        dataItem: props.dataItem,
        field: props.field,
        syntheticEvent: e.syntheticEvent,
        value: e.target.value.value,
      });
    }
  };

  const { dataItem } = props;
  const field = props.field || "";
  const dataValue = dataItem[field] === null ? "" : dataItem[field];

  const options = INCOME_TYPES.map(item => {

    let label = item.text;
    if (item.value === "OTHER" && dataItem.incomeTypeOtherName) {
      label = dataItem.incomeTypeOtherName;
    }
    return {
      ...item,
      text: label,
    }
  });

  if (!dataItem.inEdit) {
    const selectedOption = options.find((c) => c.value === dataValue);
    return <td>
      {selectedOption.text}
    </td>
  }

  return (
    <td>
      <DropDownList
        style={{
          width: "140px",
        }}
        onChange={handleChange}
        value={options.find((c) => c.value === dataValue)}
        data={options}
        textField="text"
      />
    </td>
  );
};

const TaxedDropDown = (props) => {
  const { dataItem } = props;
  const field = props.field || "";
  const [state, setState] = useState({
    dataValue: dataItem[field] === null ? "" : dataItem[field]
  })
  const handleChange = (e) => {
    if (props.onChange) {
      props.onChange({
        dataIndex: 0,
        dataItem: props.dataItem,
        field: props.field,
        syntheticEvent: e.syntheticEvent,
        value: e.target.value.value,
      });
    }
  };

  const options = [
    {
      value: 0,
      text: "0%",
    },
    {
      value: 50,
      text: "50%",
    },
    {
      value: 85,
      text: "85%",
    },
    {
      value: 100,
      text: "100%",
    },
  ];

  useEffect(() => {
    if (props.dataItem.incomeType === "SOCIAL_SECURITY" && props.dataItem.prevIncomeType.taxType !== "SOCIAL_SECURITY") {
      setState((v) => ({ ...v, dataValue: 85 }))
      TaxedDropDown.update("taxed")(dataItem, 85);
    } else if (props.dataItem.incomeType !== "SOCIAL_SECURITY" && props.dataItem.prevIncomeType.taxType === "SOCIAL_SECURITY") {
      setState((v) => ({ ...v, dataValue: 100 }))
      TaxedDropDown.update("prevIncomeType")(dataItem, "noValid");
    } else if (props.dataItem.incomeType !== props.dataItem.prevIncomeType.taxType) {
      TaxedDropDown.update("taxed")(dataItem, 100);
      setState((v) => ({ ...v, dataValue: 100 }))
    }
  }, [props.dataItem.incomeType])


  useEffect(() => {
    setState((v) => ({ ...v, dataValue: props.dataItem.taxed }))
  }, [props.dataItem.taxed])



  if (!dataItem.inEdit) {
    const selectedOption = options.find((c) => c.value === state.dataValue);
    return <td>
      {selectedOption.text}
    </td>
  }

  return (
    <td>
      <DropDownList
        style={{
          width: "100px",
        }}
        onChange={handleChange}
        value={options.find((c) => c.value === state.dataValue)}
        data={options}
        textField="text"
      />
    </td>
  );
};

const Income = (props) => {
  const [state, setState] = useState({
    sort: [
      {
        field: "firstName",
        dir: "asc",
      },
    ],
    page: { skip: 0, take: 10 },
    errorMessages: [],
  });

  const [open, setOpen] = useState({
    dataItem: null,
    show: false,
    label: null,
  });

  const [trustDialogParams, setTrustDialogParams] = useState({
    isOpen: false,
    dataItem: null
  });

  useEffect(() => {
    setState({
      ...state,
      errorMessages: collectErrorMessages(props.validations.incomes, "income"),
    })
  }, [props.validations.incomes]);

  const addIncome = () => {
    const prototype = {
      id: null,
      editIndex: 0,
      inEdit: true,
      amount: 0,
      endYear: getCurrentYear(),
      incomeType: "PENSION",
      frequency: "YEARLY",
      startYear: getCurrentYear(),
      owner: "CLIENT",
      taxed: 100,
      prevIncomeType: { taxType: "noValid", rate: 0 },
    };

    props.addIncome(prototype);
  };

  const handleOtherDialogCancel = () => {
    setOpen({
      ...open,
      show: false,
    })
  }

  const handleOtherDialogSave = () => {
    props.update("incomeTypeOtherName")(open.dataItem, open.label);
    setOpen({
      dataItem: null,
      show: false,
      label: null,
    });
  }

  const openAddTrustDialog = (dataItem) => {
    setTrustDialogParams({
      isOpen: true,
      dataItem: dataItem
    });
  };

  OwnerJointTrustDropDown.hasSpouse = props.client.hasSpouse;

  TaxedDropDown.update = props.update

  const incomes = props.incomes.map((item, index) => {
    const validation = props.validations.incomes[index];
    let isValid = true;
    if (validation) {
      Object.keys(validation).forEach((key) => {
        if (!validation[key].valid) isValid = false;
      });
    }
    return {
      ...item,
      inEdit: props.readOnly ? false : true,
      showJointTrustOptions: item.incomeType === "OTHER",
      isValid: isValid
    }
  });

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Typography component={"h1"}>Incomes</Typography>
      </Grid>
      <Grid item xs={12}>
        {(!props.readOnly && state.errorMessages.length > 0) && (
            <div className="error-tooltip-wrapper">
              <StyledTooltip
                  arrow
                  className="error-tooltip"
                  placement="right"
                  title={
                    <ul className="error-tooltip-ul">{
                      state.errorMessages.map((e, idx) => {
                        return (
                            <li key={idx}>
                              <Typography
                                  className={'default-font'}
                                  textAlign={'left'}
                                  variant={'span'}> {e}
                              </Typography>
                            </li>
                        );
                      })}
                    </ul>
                  }
              >
                <IconButton>
                  <ErrorIcon />
                </IconButton>
              </StyledTooltip>
            </div>
        )}
        {!props.readOnly && <Button onClick={addIncome}>
          <AddIcon />
          Add Income
        </Button>}
      </Grid>
      <Grid item xs={12}>
        <DataGrid
          data={orderBy(incomes, state.sort).slice(
            state.page.skip,
            state.page.take + state.page.skip
          )}
          skip={state.page.skip}
          take={state.page.take}
          total={props.incomes.length}
          pageable={true}
          onPageChange={(event) => {
            setState({
              ...state,
              page: event.page,
            });
          }}
          scrollable={"none"}
          editField={"inEdit"}
          onItemChange={(event) => {
            const dataItem = event.dataItem;
            const field = event.field;
            const value = event.value;
            if (field === 'incomeType' && value === 'OTHER') {
              setOpen({
                ...open,
                dataItem: dataItem,
                show: true,
                label: dataItem.incomeTypeOtherName,
              });
            }
            props.update(field)(dataItem, value);
          }}
        >
          <GridColumn title={" "} field={"isValid"} cell={ValidationSignCell} />
          <GridColumn
            title={"Income Type"}
            field={"incomeType"}
            cell={IncomeTypeDropDown}
          />
          <GridColumn
            title={"Frequency"}
            field={"frequency"}
            cell={FrequencyDropDown}
          />
          <GridColumn title={"Owner"} field={"owner"} cell={(p) => <OwnerJointTrustDropDown {...p} openAddTrustDialog={openAddTrustDialog} allowNewTrusts={true} />} />
          <GridColumn title={"Amount"} field={"amount"} cell={CurrencyCell} />
          <GridColumn title={"Tax Rate"} field={"taxed"} cell={TaxedDropDown} />
          <GridColumn title={"Start Year"} field={"startYear"} cell={YearCell} />
          <GridColumn title={"End Year"} field={"endYear"} cell={YearCell} />
          {!props.readOnly && <GridColumn title={" "} cell={CommandCell(props.delete)} />}
        </DataGrid>
      </Grid>
      <TrustNameDialog
          open={trustDialogParams.isOpen}
          dataItem={trustDialogParams.dataItem}
          handleSave={(dataItem, name) => {

            props.update("owner")(dataItem, name);

            setTrustDialogParams({
              isOpen: false,
              dataItem: null
            });
          }}
          handleLeave={() => {
            setTrustDialogParams({
              isOpen: false,
              dataItem: null
            });
          }}
      />
      {open.show && (
          <Dialog
              fullWidth={true}
              maxWidth={"sm"}
              open={open.show}
              aria-labelledby="modal-modal-title"
              aria-describedby="modal-modal-description"
          >
            <DialogContent style={{ margin: "auto" }}>
              <Input
                  label={"Custom Label"}
                  maxLength={25}
                  value={open.label}
                  onChange={(e) => {
                    setOpen({
                      ...open,
                      label: e.target.value,
                    })
                  }}
              />
            </DialogContent>
            <DialogActions style={{ display: 'flex', justifyContent: 'center' }}>
              <Button onClick={handleOtherDialogCancel}>
                Cancel
              </Button>
              <Button onClick={handleOtherDialogSave}>
                Save
              </Button>
            </DialogActions>
          </Dialog>
      )}
    </Grid>
  );
};

export default Income;
